
import { Component, Prop } from 'vue-property-decorator';
import { AlertEscalationTypeEnum, AlertTimingEnum, AlertTypeEnum, LinearAlertModel, TenantUserModel } from '@/libs/Api';
import { linearAlertsStore } from '@/libs/alerts/+state/store';
import { CrudAction, CrudReponse } from '@/libs/core/+state/models/crud-action';
import { Guid } from '@/libs/common/functions/guid';
import Page from '@/Page.vue';

import SettingsAlertRecipientSelect from './SettingsAlertRecipientSelect.vue';

@Component({ components: { SettingsAlertRecipientSelect } })
export default class NotificationAlertsTable extends Page {
    @Prop({ required: true }) alerts!: LinearAlertModel[];
    @Prop({ required: true }) tenantUsers!: TenantUserModel[];
    @Prop({ required: true }) type!: AlertTypeEnum;
    @Prop({ default: () => AlertTimingEnum.Delay }) timing!: AlertTimingEnum;

    adding: { [key: string]: boolean } = {};
    saving: { [key: string]: boolean } = {};
    deleting: { [key: string]: boolean } = {};

    // Dialog
    dialogVisible = false;
    dialogValid = true;
    dialogSaving = false;
    dialogModel: LinearAlertModel | null = null;
    dialogOnDay = true;
    dialogIsRange = false;
    showValidation = false;

    nameRules = [(v: any) => !!v || (this.$t('validation.thisFieldIsRequired') as string)];
    daysRules = [
        (v: any) => (v !== null && v !== undefined) || (this.$t('validation.thisFieldIsRequired') as string),
        (v: any) => (v === null || v === undefined || isNaN(Number(v)) ? (this.$t('common.enterAnInteger') as string) : true),
        (v: any) => (Number(v) >= 1 ? true : (this.$t('common.mustBeMoreOrEqual', { value: 1 }) as string)),
    ];
    get daysToRules() {
        return [
            (v: any) => (this.dialogIsRange ? v !== null && v !== undefined : true) || (this.$t('validation.thisFieldIsRequired') as string),
            (v: any) => (!this.dialogIsRange || v === null || v === undefined || !isNaN(Number(v)) ? true : (this.$t('common.enterAnInteger') as string)),
            (v: any) => (!this.dialogIsRange || Number(v) >= 1 ? true : (this.$t('common.mustBeMoreOrEqual', { value: 1 }) as string)),
            (v: any) => {
                if (!this.dialogIsRange) return true;
                const from = Number(this.dialogModel?.daysDelay ?? 0);
                const to = Number(v);
                return to >= from ? true : (this.$t('settings.categories.daysRangeInvalid') as string);
            },
        ];
    }
    recipientRules = [(v: any) => (Array.isArray(v) && v.length > 0) || (this.$t('validation.thisFieldIsRequired') as string)];
    protected AlertTimingEnum: typeof AlertTimingEnum = AlertTimingEnum;

    // Delete confirm
    deleteDialog = false;
    selectedToDelete: LinearAlertModel | null = null;
    deletingSelected = false;

    get alertsByType(): LinearAlertModel[] {
        const linq = this.alerts as any;
        if (linq && typeof linq.where === 'function') {
            return linq
                .where((x: LinearAlertModel) => x.type == this.type && (x.alertTiming ?? AlertTimingEnum.Delay) == this.timing)
                .toArray();
        }
        return (this.alerts ?? []).filter(
            (x) => x.type == this.type && (x.alertTiming ?? AlertTimingEnum.Delay) == this.timing
        );
    }

    get sectionTitleKey(): string {
        if (this.timing === AlertTimingEnum.Delay) {
            switch (this.type) {
                case AlertTypeEnum.Performance:
                    return 'settings.categories.alertsPerformance';
                case AlertTypeEnum.Finding:
                    return 'settings.categories.alertsFinding';
                case AlertTypeEnum.MeasureImplementation:
                    return 'settings.categories.alertsMeasureImplementation';
                default:
                    return 'settings.categories.notificationAlerts';
            }
        } else {
            switch (this.type) {
                case AlertTypeEnum.Performance:
                    return 'settings.categories.upcomingPerformance';
                case AlertTypeEnum.Finding:
                    return 'settings.categories.upcomingFinding';
                case AlertTypeEnum.MeasureImplementation:
                    return 'settings.categories.upcomingMeasureImplementation';
                default:
                    return 'settings.categories.upcomingAlerts';
            }
        }
    }

    openAddDialog(type: AlertTypeEnum) {
        const nextIndex =
            (this.alerts ?? []).filter((x) => x.type == type && (x.alertTiming ?? AlertTimingEnum.Delay) == this.timing)
                .length + 1;
        this.dialogModel = {
            id: Guid.EmptyGuid(),
            name: this.$t('settings.categories.level', { index: nextIndex }) as string,
            daysDelay: this.timing === AlertTimingEnum.Delay ? 1 : 0,
            type: type,
            alertTiming: this.timing,
            alertEscalations: this.timing === AlertTimingEnum.Before ? [{ type: AlertEscalationTypeEnum.Owner } as any] : [],
        } as LinearAlertModel;
        this.dialogOnDay = this.timing === AlertTimingEnum.Before;
        this.dialogIsRange = false;
        if (this.dialogModel) {
            this.dialogModel.daysDelayTo = null as any;
            (this.dialogModel as any).everyDay = false;
        }
        this.dialogVisible = true;
    }

    openEditDialog(alert: LinearAlertModel) {
        this.dialogModel = JSON.parse(JSON.stringify(alert));
        const model = this.dialogModel as LinearAlertModel;
        this.dialogOnDay =
            (model.alertTiming ?? AlertTimingEnum.Delay) === AlertTimingEnum.Before && (model.daysDelay ?? 0) === 0;
        if ((model.alertTiming ?? AlertTimingEnum.Delay) === AlertTimingEnum.Before) {
            model.alertEscalations = [{ type: AlertEscalationTypeEnum.Owner } as any];
        }
        // Normalize everyDay to boolean
        (model as any).everyDay = (model as any).everyDay === true;
        this.dialogIsRange = (model.daysDelayTo ?? null) != null;
        this.dialogVisible = true;
    }

    closeDialog() {
        this.dialogVisible = false;
        this.dialogModel = null;
        this.showValidation = false;
    }

    get dialogHeader(): string {
        return this.dialogModel && this.dialogModel.id == Guid.EmptyGuid()
            ? (this.$t('settings.categories.addLevel') as string)
            : this.dialogModel?.name ?? '';
    }

    saveDialog() {
        if (!this.dialogModel) return;
        this.showValidation = true;
        const form: any = this.$refs.form;
        if (form && typeof form.validate === 'function') {
            const ok = form.validate();
            if (!ok || !this.hasRecipients) {
                return;
            }
        } else if (!this.hasRecipients) {
            return;
        }
        if (this.timing === AlertTimingEnum.Before) {
            this.dialogModel.alertEscalations = [{ type: AlertEscalationTypeEnum.Owner } as any];
            if (this.dialogOnDay === true) {
                this.dialogModel.daysDelay = 0;
                this.dialogModel.daysDelayTo = null as any;
                (this.dialogModel as any).everyDay = false as any;
            }
        }
        if (!this.dialogIsRange && this.dialogModel) {
            this.dialogModel.daysDelayTo = null as any;
            (this.dialogModel as any).everyDay = false as any;
        }
        if (this.dialogIsRange && this.dialogModel) {
            const from = Number(this.dialogModel.daysDelay ?? 0);
            const to = Number(this.dialogModel.daysDelayTo ?? 0);
            if (to < from) {
                this.dialogModel.daysDelayTo = from as any;
            }
        }
        const isCreate = !this.dialogModel.id || this.dialogModel.id == Guid.EmptyGuid();
        this.dialogSaving = true;
        if (isCreate) {
            const item = { ...this.dialogModel } as LinearAlertModel;
            linearAlertsStore.dispatch(CrudAction.Create, { item });
            this.subscribe(linearAlertsStore, CrudReponse.Create)
                .then(() => {
                    this.$emit('changed');
                    this.$emit('saved');
                    this.closeDialog();
                })
                .finally(() => (this.dialogSaving = false));
        } else {
            linearAlertsStore.dispatch(CrudAction.Update, { item: this.dialogModel });
            this.subscribe(linearAlertsStore, CrudReponse.Update)
                .then(() => {
                    this.$emit('changed');
                    this.$emit('saved');
                    this.closeDialog();
                })
                .finally(() => (this.dialogSaving = false));
        }
    }

    confirmDelete(alert: LinearAlertModel) {
        this.selectedToDelete = alert;
        this.deleteDialog = true;
    }

    doDeleteSelected() {
        if (!this.selectedToDelete || !this.selectedToDelete.id) {
            this.deleteDialog = false;
            return;
        }
        const id = this.selectedToDelete.id;
        this.deleting[id] = true;
        this.deletingSelected = true;
        linearAlertsStore.dispatch(CrudAction.Delete, { id });
        this.subscribe(linearAlertsStore, CrudReponse.Delete)
            .then(() => {
                this.$emit('changed');
                this.deleteDialog = false;
                this.selectedToDelete = null;
            })
            .finally(() => {
                this.deleting[id] = false;
                this.deletingSelected = false;
            });
    }

    isDeleting(id?: string): boolean {
        return !!(id && this.deleting[id]);
    }

    getAlertRecipientDisplay(alert: LinearAlertModel): string {
        const parts: string[] = [];
        const escalations = (alert.alertEscalations ?? []) as any[];
        if (escalations.some((x) => x.type === AlertEscalationTypeEnum.Owner)) {
            parts.push(this.$t('settings.categories.escalationType.owner') as string);
        }
        if (escalations.some((x) => x.type === AlertEscalationTypeEnum.DirectSupervisor)) {
            parts.push(this.$t('settings.categories.escalationType.directSupervisor') as string);
        }
        if (escalations.some((x) => x.type === AlertEscalationTypeEnum.SecondLevelSupervisor)) {
            parts.push(this.$t('settings.categories.escalationType.secondLevelSupervisor') as string);
        }
        if (escalations.some((x) => x.type === AlertEscalationTypeEnum.ThirdLevelSupervisor)) {
            parts.push(this.$t('settings.categories.escalationType.thirdLevelSupervisor') as string);
        }
        const ids = (alert.alertEscalations ?? [])
            .filter((x: any) => x.type === AlertEscalationTypeEnum.TenantUser && !!x.tenantUserId)
            .map((x: any) => x.tenantUserId as string);
        const map = new Map(this.tenantUsers.map((u) => [u.id!, u.user?.displayName ?? '']));
        const users = ids.map((id: string) => map.get(id) ?? '').filter((n: string) => !!n);
        parts.push(...users);
        return parts.join(', ');
    }

    get hasRecipients(): boolean {
        const count = (this.dialogModel?.alertEscalations ?? []).length;
        return count > 0;
    }
}
